import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore'
import { db } from '@/firebase'

export function parseItemPrice(v) {
  if (v == null || v === '') return 0
  const n = parseFloat(String(v).replace(/,/g, '.'))
  return Number.isFinite(n) ? n : 0
}

/** نسبة الخصم على الصنف (discount = %) */
export function parseItemDiscountPercent(item) {
  const d = parseItemPrice(item?.discount)
  return d > 0 ? d : 0
}

/** السعر بعد خصم النسبة — discount نسبة مئوية */
export function computePriceAfterDiscount(rawPrice, discountPercent) {
  const price = parseItemPrice(rawPrice)
  const pct = parseItemPrice(discountPercent)
  if (price <= 0) return 0
  if (pct <= 0) return price
  if (pct >= 100) return 0
  const after = price * (1 - pct / 100)
  return Math.max(0, Math.round(after * 100) / 100)
}

export function formatItemPriceStored(n) {
  const v = parseItemPrice(n)
  if (v <= 0) return '0'
  return v % 1 === 0 ? String(v) : v.toFixed(2)
}

export function isItemPubliclyVisible(item) {
  if (!item || typeof item !== 'object') return false
  return item.active !== false && item.active !== 'false'
}

/** سعر الصنف الأساسي (بعد الخصم إن وُجد) — يُستخدم لحساب سعر كل حجم */
export function resolveItemBasePrice(item) {
  const raw = parseItemPrice(item?.price)
  const discountPct = parseItemDiscountPercent(item)

  if (raw > 0 && discountPct > 0) {
    return computePriceAfterDiscount(raw, discountPct)
  }

  const afterDiscount = parseItemPrice(item?.priceafterdiscount)
  if (afterDiscount > 0) return afterDiscount

  return raw > 0 ? raw : 0
}

function readSizeStoredPrice(...values) {
  for (const v of values) {
    const n = parseItemPrice(v)
    if (v != null && v !== '') return n
  }
  return 0
}

/** هل مصفوفة sizes.price تخزّن أسعاراً كاملة وليس فروقاً؟ */
function sizesStoredAsFullPrices(storedPrices, base) {
  if (base <= 0) return true
  const parsed = storedPrices.map((v) => parseItemPrice(v))
  if (!parsed.length) return false
  return parsed.some((p) => p >= base)
}

function toFullSizePrice(base, stored, mode) {
  if (mode === 'full') return stored
  return base + stored
}

/** أحجام الصنف بأسعار كاملة للعرض (الأساسي + فرق الحجم في Firestore) */
export function itemSizesForDisplay(item, options = {}) {
  const base =
    options.basePrice != null
      ? parseItemPrice(options.basePrice)
      : resolveItemBasePrice(item)
  const sizes = item?.sizes
  if (!sizes || typeof sizes !== 'object') return []

  if (Array.isArray(sizes.name) || Array.isArray(sizes.name_ar)) {
    const names = sizes.name || []
    const namesAr = sizes.name_ar || []
    const stored = sizes.price || sizes.pricebeforediscount || []
    const len = Math.max(names.length, namesAr.length, stored.length)
    const mode = sizesStoredAsFullPrices(stored, base)
    const list = []
    for (let i = 0; i < len; i++) {
      const storedPrice = readSizeStoredPrice(stored[i])
      list.push({
        name: String(names[i] ?? '').trim(),
        name_ar: String(namesAr[i] ?? names[i] ?? '').trim(),
        price: toFullSizePrice(base, storedPrice, mode)
      })
    }
    return list.filter((s) => s.name || s.name_ar)
  }

  const objectEntries = Object.values(sizes).flatMap((sizeData) => {
    if (!sizeData || typeof sizeData !== 'object') return []
    const storedPrice = readSizeStoredPrice(
      sizeData.price?.[0],
      sizeData.pricebeforediscount?.[0],
      sizeData.price,
      sizeData.pricebeforediscount
    )
    return [
      {
        name: String(sizeData.name?.[0] ?? sizeData.name ?? '').trim(),
        name_ar: String(sizeData.name_ar?.[0] ?? sizeData.name_ar ?? '').trim(),
        storedPrice
      }
    ]
  })

  if (!objectEntries.length) return []

  const mode = sizesStoredAsFullPrices(
    objectEntries.map((e) => e.storedPrice),
    base
  )

  return objectEntries.map((entry) => ({
    name: entry.name,
    name_ar: entry.name_ar,
    price: toFullSizePrice(base, entry.storedPrice, mode)
  }))
}

/** أقل سعر حجم للعرض — beforeDiscount: السعر قبل خصم الصنف */
export function itemMinSizePrice(item, { beforeDiscount = false } = {}) {
  const basePrice = beforeDiscount ? parseItemPrice(item?.price) : undefined
  const sizes = itemSizesForDisplay(
    item,
    basePrice > 0 ? { basePrice } : {}
  )
  const prices = sizes.map((s) => parseItemPrice(s.price)).filter((p) => p > 0)
  if (prices.length) return Math.min(...prices)
  return beforeDiscount ? parseItemPrice(item?.price) : resolveItemBasePrice(item)
}

/**
 * بيانات عرض السعر في المنيو — currentPrice يُمرَّر من الواجهة (بعد الخصم)
 * @param {object} [options]
 * @param {number|string} [options.originalOverride] — السعر قبل الخصم (مثلاً أقل حجم)
 */
export function itemPriceDisplayMeta(item, currentPrice, options = {}) {
  const current = parseItemPrice(currentPrice)
  const discountPct = parseItemDiscountPercent(item)
  const original =
    options.originalOverride != null
      ? parseItemPrice(options.originalOverride)
      : parseItemPrice(item?.price)

  const hasDiscount =
    discountPct > 0 &&
    original > 0 &&
    current > 0 &&
    current < original - 0.009

  return {
    discountPercent: discountPct,
    hasDiscount,
    originalPrice: original,
    currentPrice: current
  }
}

export function formatMoney(n) {
  const v = parseItemPrice(n)
  return v % 1 === 0 ? String(v) : v.toFixed(2)
}

const ITEM_IMAGE_FALLBACK =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80"><rect fill="#334155" width="80" height="80" rx="8"/><text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle" fill="#94a3b8" font-size="10">صنف</text></svg>'
  )

/** صورة الصنف للعرض في المنيو والسلة */
export function resolvePublicItemImageUrl(item, restaurant) {
  const coverNorm = String(restaurant?.cat_image || '')
    .trim()
    .split('?')[0]
  const imgs = item?.item_image
  const urls = []
  if (Array.isArray(imgs)) {
    urls.push(...imgs.map((u) => String(u || '').trim()).filter(Boolean))
  } else if (typeof imgs === 'string' && imgs.trim()) {
    urls.push(imgs.trim())
  }
  // 1) صورة خاصة بالصنف
  const own = urls.find((u) => u.split('?')[0] !== coverNorm)
  if (own) return own

  // 2) صورة القسم التابع له الصنف
  const subIndex = item?.item_cat_sub != null ? Number(item.item_cat_sub) : null
  if (subIndex != null && subIndex >= 0) {
    const subcatImages = restaurant?.subcat?.images || []
    const secImg = String(subcatImages[subIndex] || '').trim()
    if (secImg && secImg.split('?')[0] !== coverNorm) {
      return secImg
    }
  }

  // 3) غلاف المطعم الرئيسي
  const cat = String(restaurant?.cat_image || '').trim()
  if (cat) return cat

  // 4) صورة افتراضية
  return ITEM_IMAGE_FALLBACK
}

export function publicItemImageFallback() {
  return ITEM_IMAGE_FALLBACK
}

function parseTimeToMinutes(timeStr) {
  const s = String(timeStr ?? '').trim()
  const m = s.match(/^(\d{1,2}):(\d{2})/)
  if (!m) return null
  const h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (h < 0 || h > 23 || min < 0 || min > 59) return null
  return h * 60 + min
}

/** وقت بصيغة 12 ساعة عربية: 9:00 ص ، 2:30 م */
export function formatClockAmPmAr(timeStr) {
  const mins = parseTimeToMinutes(timeStr)
  if (mins == null) {
    const raw = String(timeStr ?? '').trim()
    return raw || '—'
  }
  let h24 = Math.floor(mins / 60) % 24
  const min = mins % 60
  const isPm = h24 >= 12
  let h12 = h24 % 12
  if (h12 === 0) h12 = 12
  const mm = String(min).padStart(2, '0')
  return `${h12}:${mm} ${isPm ? 'م' : 'ص'}`
}

export function formatOpenHours(restaurant) {
  if (!restaurant) return ''
  if (restaurant.allhour) return 'مفتوح 24 ساعة'
  const from = restaurant.OpenTime?.From || restaurant.OpenTime?.from || ''
  const to = restaurant.OpenTime?.To || restaurant.OpenTime?.to || ''
  if (from && to) {
    return `${formatClockAmPmAr(from)} — ${formatClockAmPmAr(to)}`
  }
  if (from) return `من ${formatClockAmPmAr(from)}`
  if (to) return `حتى ${formatClockAmPmAr(to)}`
  return '—'
}

/** حالة الفتح الحالية حسب التوقيت المحلي */
export function getRestaurantOpenStatus(restaurant, now = new Date()) {
  if (!restaurant) {
    return { isOpen: null, label: '—', hoursText: '—' }
  }

  const hoursText = formatOpenHours(restaurant)

  if (restaurant.active === false || restaurant.active === 'false') {
    return { isOpen: false, label: 'مغلق', hoursText }
  }

  if (restaurant.allhour) {
    return { isOpen: true, label: 'مفتوح الآن', hoursText }
  }

  if (restaurant.closefriday && now.getDay() === 5) {
    return { isOpen: false, label: 'مغلق اليوم', hoursText }
  }

  const from = parseTimeToMinutes(restaurant.OpenTime?.From || restaurant.OpenTime?.from)
  const to = parseTimeToMinutes(restaurant.OpenTime?.To || restaurant.OpenTime?.to)

  if (from == null || to == null) {
    return { isOpen: null, label: '—', hoursText }
  }

  const current = now.getHours() * 60 + now.getMinutes()
  let isOpen
  if (from === to) {
    isOpen = true
  } else if (from < to) {
    isOpen = current >= from && current < to
  } else {
    isOpen = current >= from || current < to
  }

  return {
    isOpen,
    label: isOpen ? 'مفتوح الآن' : 'مغلق الآن',
    hoursText
  }
}

export function restaurantSlogan(restaurant) {
  const d = restaurant?.data
  if (!d || typeof d !== 'object') return ''
  return String(d.slogan || d.short_description || '').trim()
}

export function resolveItemSectionName(item, restaurant) {
  if (!item) return ''
  if (item.sectionName) return String(item.sectionName).trim()
  const subIdx = item.item_cat_sub != null ? Number(item.item_cat_sub) : -1
  if (subIdx < 0) return ''
  const subcats = restaurant?.subcat?.ar || restaurant?.data?.subcat?.ar || (Array.isArray(restaurant?.subcat) ? restaurant.subcat : [])
  return subcats[subIdx] ? String(subcats[subIdx]).trim() : ''
}

export function buildMenuSections(restaurant, items) {
  const subcatAr = restaurant?.subcat?.ar || []
  const subcatImages = restaurant?.subcat?.images || []
  const activeItems = (items || []).filter(isItemPubliclyVisible)
  const bySub = new Map()

  for (const item of activeItems) {
    const sub = item.item_cat_sub != null ? Number(item.item_cat_sub) : 0
    if (!bySub.has(sub)) bySub.set(sub, [])
    bySub.get(sub).push(item)
  }

  const sections = []
  bySub.forEach((list, subIndex) => {
    sections.push({
      id: subIndex,
      title: subcatAr[subIndex] || (subIndex === 0 ? 'المنيو' : `قسم ${subIndex + 1}`),
      imageUrl: String(subcatImages[subIndex] || '').trim(),
      items: list.map((it) => ({ ...it, sectionName: subcatAr[subIndex] || (subIndex === 0 ? 'المنيو' : 'قسم ' + (subIndex + 1)) })).sort((a, b) =>
        String(a.name_ar || a.name || '').localeCompare(String(b.name_ar || b.name || ''), 'ar')
      )
    })
  })

  sections.sort((a, b) => a.id - b.id)
  return sections
}

export async function fetchRestaurantPublicProfile(restaurantId) {
  const id = String(restaurantId || '').trim()
  if (!id) throw new Error('معرّف المطعم غير صالح')

  const snap = await getDoc(doc(db, 'categories', id))
  if (!snap.exists()) throw new Error('المطعم غير موجود')

  const restaurant = { id: snap.id, ...snap.data() }
  const itemsSnap = await getDocs(query(collection(db, 'items'), where('item_cat', '==', id)))
  const items = itemsSnap.docs.map((d) => ({ id: d.id, ...d.data() }))

  return {
    restaurant,
    items,
    sections: buildMenuSections(restaurant, items)
  }
}

/** تحميل بروفايل LMenus بالسلاج (اسم المطعم في الرابط) */
export async function fetchRestaurantPublicProfileBySlug(slug) {
  const key = String(slug || '').trim()
  if (!key) throw new Error('رابط غير صالح')

  const snap = await getDocs(
    query(collection(db, 'categories'), where('lmenusSlug', '==', key), limit(1))
  )
  if (snap.empty) throw new Error('المطعم غير موجود')

  const docSnap = snap.docs[0]
  return fetchRestaurantPublicProfile(docSnap.id)
}
